import CssMatrix from "../interface/CssMatrix";
import FunctionHelper from "./FunctionHelper";

export default class DomHelper {
	public static readonly SCROLLBAR_SIZE: number = 17;

	/**
	 * Find a dom child of a given root node via query
	 */
	public static find(root: HTMLElement | null | undefined, selector: string): HTMLElement | null {
		if (typeof root === "undefined" || root === null) {
			return null;
		}

		return root.querySelector(selector);
	}

	public static findId(id: string) {
		return document.getElementById(id);
	}

	/**
	 * Find dom childs of a given root node via query
	 */
	public static findAll(root: HTMLElement | null | undefined, selector: string): Array<HTMLElement> {
		let result: Array<HTMLElement> = [];

		if (typeof root === "undefined" || root === null) {
			return result;
		}

		let list: NodeList = FunctionHelper.applyValue(root.querySelectorAll(selector), []);

		for (let element of list.values()) {
			result.push(element as HTMLElement);
		}

		return result;
	}

	/**
	 * Finds next dom siblings of a given root node via query
	 */
	public static next(root: HTMLElement | null | undefined, selector: string): HTMLElement | null {
		if (typeof root === "undefined" || root === null) {
			return null;
		}

		let sibling: Element | null = root.nextElementSibling;

		while (sibling !== null) {
			if (sibling.matches(selector)) {
				return sibling as HTMLElement;
			}

			sibling = sibling.nextElementSibling;
		}

		return null;
	}

	/**
	 * Finds previous dom siblings of a given root node via query
	 */
	public static previous(root: HTMLElement | null | undefined, selector: string): HTMLElement | null {
		if (typeof root === "undefined" || root === null) {
			return null;
		}

		let sibling: Element | null = root.previousElementSibling;

		while (sibling !== null) {
			if (sibling.matches(selector)) {
				return sibling as HTMLElement;
			}

			sibling = sibling.previousElementSibling;
		}

		return null;
	}

	public static html(element: HTMLElement | SVGElement | null | undefined, html: string): void {
		if (typeof element === "undefined" || element === null) {
			return;
		}

		element.innerHTML = html;
	}

	/**
	 * Find dom parent of a given root node via query
	 */
	public static parent(root: HTMLElement | null | undefined, selector: string): HTMLElement | null {
		if (typeof root === "undefined" || root === null) {
			return null;
		}

		let child: HTMLElement | null = root;

		while (child !== null) {
			if (child.matches(selector)) {
				return child;
			}

			child = child.parentElement;
		}

		return null;
	}

	/**
	 * Deletes a dom element or dom elements
	 */
	public static delete(element: HTMLElement | Array<HTMLElement> | null | undefined): void {
		if (typeof element === "undefined" || element === null) {
			return;
		}

		let target: Array<HTMLElement> = (!Array.isArray(element)) ? [element] : element;

		for (let entry of target.values()) {
			if (entry.parentNode !== null) {
				entry.parentNode.removeChild(entry);
			}
		}
	}

	/**
	 * Create a dom element of a html string
	 */
	public static createElement(html: string): ChildNode | null {
		let tempElement: HTMLDivElement = window.document.createElement('div');
		tempElement.innerHTML = html;
		return tempElement.firstChild;
	}

	/**
	 * Append a HTMLElement to a parent element
	 */
	public static append(
		parent: HTMLElement | SVGElement | null | undefined, element: HTMLElement | SVGElement | null | undefined
	): void {
		if (typeof parent === "undefined" || parent === null || typeof element === "undefined" || element === null) {
			return;
		}

		parent.appendChild(element);
	}

	/**
	 * Check if a HTMLElement has the give css class
	 */
	public static hasClass(element: HTMLElement | null | undefined, cssClass: string): boolean {
		if (typeof element === "undefined" || element === null) {
			return false;
		}

		return new RegExp('(^| )' + cssClass + '( |$)').test(element.className);
	}

	/**
	 * Adds css classes to a HTMLElement or HTMLElements
	 */
	public static addClass(element: HTMLElement | Array<HTMLElement> | null | undefined, cssClasses: string): void {
		if (typeof element === "undefined" || element === null) {
			return;
		}

		let target: Array<HTMLElement> = (!Array.isArray(element)) ? [element] : element;

		let classes = cssClasses.split(' ');
		for (let cssClass of classes.values()) {
			for (let entry of target.values()) {
				if (!this.hasClass(entry, cssClass)) {
					entry.classList.add(cssClass);
				}
			}
		}
	}

	/**
	 * Removes css classes from a HTMLElement or HTMLElements
	 */
	public static removeClass(element: HTMLElement | Array<HTMLElement> | null | undefined, cssClasses: string): void {
		if (typeof element === "undefined" || element === null) {
			return;
		}

		let target: Array<HTMLElement> = (!Array.isArray(element)) ? [element] : element;

		let classes = cssClasses.split(' ');
		for (let cssClass of classes.values()) {
			for (let entry of target.values()) {
				if (this.hasClass(entry, cssClass)) {
					entry.classList.remove(cssClass);
				}
			}
		}
	}

	/**
	 * Adds or removes css classes to a HTMLElement or HTMLElements if the condition is met or not
	 */
	public static setConditionalClass(
		element: HTMLElement | Array<HTMLElement> | null | undefined, cssClasses: string, condition: boolean
	): void {
		if (condition) {
			this.addClass(element, cssClasses);
		} else {
			this.removeClass(element, cssClasses);
		}
	}

	/**
	 * gets or sets a data attribute of an HTMLElement
	 */
	public static data(element: HTMLElement | null | undefined, key: string, value?: string): string | null {
		if (typeof element === "undefined" || element === null) {
			return null;
		}

		if (element.dataset) {
			if (FunctionHelper.exists(value)) {
				element.dataset[key] = value;
			} else {
				return element.dataset[key] || null;
			}
		} else {
			if (FunctionHelper.exists(value)) {
				element.setAttribute('data-' + key, value!);
			} else {
				return element.getAttribute('data-' + key);
			}
		}

		return null;
	}

	/**
	 * Generate a unique identifier
	 */
	public static generateUUID(plain?: boolean): string {
		let date: number = new Date().getTime();
		let result: string = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			let random: number = (date + Math.random() * 16) % 16 | 0;
			date = Math.floor(date / 16);
			return (c === 'x' ? random : (random && (0x3 || 0x8))).toString(16);
		});

		if (plain) {
			result = result.replaceAll("-", "");
		}

		return result;
	}

	/**
	 * Decompose a css Matrix style attribute, to it's components
	 */
	public static decomposeCssMatrix(style: string): CssMatrix | null {
		let matrix: RegExpMatchArray | null | undefined =
			style.match(/matrix\((.*?),(.*?),(.*?),(.*?),(.*?),(.*?)\)/);

		if (FunctionHelper.exists(matrix) && matrix !== null) {
			let angle: number = Math.atan2(Number(matrix[2]), Number(matrix[1]));
			let denom: number = Math.pow(Number(matrix[1]), 2) + Math.pow(Number(matrix[2]), 2);
			let scaleX: number = Math.sqrt(denom);
			let scaleY: number =
				(Number(matrix[1]) * Number(matrix[4]) - Number(matrix[3]) * Number(matrix[2])) / scaleX;
			let skewX: number =
				Math.atan2(Number(matrix[1]) * Number(matrix[3]) + Number(matrix[2]) * Number(matrix[4]), denom);

			return {
				angle: angle / (Math.PI / 180),
				scaleX: scaleX,
				scaleY: scaleY,
				skewX: skewX / (Math.PI / 180),
				skewY: 0,
				translateX: parseInt(matrix[5], 10),
				translateY: parseInt(matrix[6], 10)
			};
		}

		return null;
	};

	public static encodeHtmlEntities(value: string): string {
		let tempElement: HTMLTextAreaElement = document.createElement("textarea");
		tempElement.innerText = value;
		return tempElement.innerHTML;
	}

	public static decodeHtmlEntities(value: string): string {
		let tempElement: HTMLTextAreaElement = document.createElement("textarea");
		tempElement.innerHTML = value;
		return tempElement.value;
	}
}